import { connect } from 'react-redux'
import React from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import idx from 'idx'
import { withAuth } from '@praxis/component-auth'
import apiConfig from '../apiConfig'
import Delayed from '../components/DelayedRenders'
import { showNotification } from '../store/Notification/actionCreator'

const TEXT_ERROR_DEFAULT =
  'Photobridge is experiencing a problem.<br /> Please try again.'

/*

Interceptor adds useful handlers to axios requests and responses.

If the user has an accessToken, it is used as the value for the
Authorization header. This applies to all requests to .target.com and
.tgt domains. You can disable this per request by passing in this config:
{headers: {Authorization: false}}

If an axios response is an error, it is handled based on the error code:
- 401: prompt the user to sign in
- 403: notify the user that they don't have permission to do that
- Other: show the axios error message to the user

When the component unmounts, the interceptors are removed.

*/
export class Interceptor extends React.Component {
  static state = {
    authInterceptor: undefined,
    errorInterceptor: undefined,
  }

  componentDidMount() {
    this.addAuthInterceptor()
    this.addErrorInterceptor()
  }

  componentDidUpdate(prevProps, prevState) {
    const { lanId } = this.props
    // Error interceptor will be added before props are mapped leaving lanId = ''
    if (prevProps.lanId !== lanId) {
      this.addErrorInterceptor()
    }
  }

  componentWillUnmount() {
    this.removeAuthInterceptor()
    this.removeErrorInterceptor()
  }

  addAuthInterceptor = () => {
    const authInterceptor = axios.interceptors.request.use(
      (config) => {
        const { auth = {}, apiKey } = this.props
        const { session = {} } = auth || {}
        const { userInfo = {} } = session || {}
        const { accessToken = '' } = userInfo
        // eslint-disable-next-line no-prototype-builtins
        if (!config.headers.hasOwnProperty('authorization')) {
          if (accessToken && this.isTargetDomain(config.url)) {
            config['headers']['Authorization'] = accessToken
          }
        } else if (!config.headers.Authorization) {
          delete config.headers.Authorization
        }
        // Fallback if config params are not defined
        if (!config.params) {
          config['params'] = new URLSearchParams()
        }
        // Fallback if apiKey is not defined
        if (!config.params?.key && apiKey) {
          const isUrlParam = !!config?.params?.entries
          const isXApiKey =
            config.headers['X-Api-key'] || config.headers['x-api-key']
          if (isUrlParam && !isXApiKey) {
            config?.params?.append('key', apiKey)
          } else if (!isXApiKey) {
            config['params']['key'] = apiKey
          }
        }
        return config
      },
      (error) => Promise.reject(error)
    )
    this.setState({ authInterceptor })
  }

  removeAuthInterceptor = () => {
    axios.interceptors.request.eject(this.state.authInterceptor)
    this.setState({ authInterceptor: undefined })
  }

  addErrorInterceptor = () => {
    const errorInterceptor = axios.interceptors.response.use(
      (response = {}) => {
        return response
      },
      (error) => {
        if (error.response) {
          const code = error.response.status
          if (code === 401) {
            const { auth = {} } = this.props
            const { login = () => {} } = auth
            login({})
          } else if (code === 403) {
            const { showNotification = () => {} } = this.props
            showNotification({
              message: 'You are not authorised to perform this action.',
              variant: 'error',
            })
          }
        }
        return Promise.reject(error)
      }
    )
    this.setState({ errorInterceptor })
  }

  removeErrorInterceptor = () => {
    axios.interceptors.request.eject(this.state.errorInterceptor)
    this.setState({ errorInterceptor: undefined })
  }

  // hostname ends with .target.com or .tgt
  isTargetDomain = (url) =>
    /^([^/]+:)?\/{2,3}[^/]+?(\.target\.com|\.tgt)(:|\/|$)/i.test(url)

  render() {
    const { auth, children } = this.props
    const { session = {} } = auth || {}
    const { userInfo = {} } = session || {}
    const { accessToken = '' } = userInfo
    const waitTime = accessToken ? 100 : 2000

    return <Delayed waitBeforeShow={waitTime}>{children}</Delayed>
  }
}

Interceptor.propTypes = {
  accessToken: PropTypes.string,
  lanId: PropTypes.string,
  promptToSignIn: PropTypes.func,
  showNotification: PropTypes.func,
  promptToNoAccess: PropTypes.func,
  xApiKey: PropTypes.string,
  apiKey: PropTypes.string,
  xIdToken: PropTypes.string,
}

const mapStateToProps = (state) => {
  const { user = {} } = state
  const { lanId = '', xIdToken = '' } = user
  return {
    accessToken: user.accessToken,
    xApiKey: apiConfig.key,
    apiKey: apiConfig.apiKey,
    lanId: lanId,
    xIdToken: xIdToken,
  }
}

const mapDispatchToProps = {
  showNotification,
}

export default withAuth()(
  connect(mapStateToProps, mapDispatchToProps)(Interceptor)
)
